import { atom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'

import type { TourPlan } from '@/features/onboarding/lib/welcome-plan'
import type { TradingExperience, WelcomeTip } from '@/features/onboarding/lib/welcome-tips'
import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { safeStorage } from '@/lib/safe-storage'

export interface WelcomeGuideState {
  /** True once the user skipped, closed or finished the guide — it never auto-opens again. */
  dismissed: boolean
  /** The answer to "Used a trading terminal before?", so a reopened guide skips the question. */
  experience: TradingExperience | null
}

/** Per-user (the `dashboard-` key prefix namespaces it) first-run welcome guide state. */
export const welcomeGuideAtom = atomWithStorage<WelcomeGuideState>(
  STORAGE_KEYS.dashboard.welcomeGuide,
  { dismissed: false, experience: null },
  safeStorage,
  { getOnInit: true }
)

/** Set to true to reopen the guide on demand (Settings → General, command palette). */
export const welcomeGuideRequestedAtom = atom(false)

// Server render: nothing to read, nowhere to write.
const NO_STORAGE = { getItem: () => null, setItem: () => {}, removeItem: () => {} }

export type WelcomeTourStage =
  | { kind: 'question' }
  // "Want us to set up a simple page?" — offered to newcomers and some experience.
  | { kind: 'setup'; templateKey: string }
  // Waiting for that page to be created and switched to.
  | { kind: 'building'; fromPageId: string | null }
  | { kind: 'step'; index: number }
  | { kind: 'finish' }

export interface WelcomeTourSession {
  experience: TradingExperience | null
  steps: WelcomeTip[]
  stage: WelcomeTourStage
}

/**
 * Where a running welcome tour is. Kept out of the component so a remount (a page switch,
 * a dev Fast Refresh, a reload) resumes the tour instead of starting it over at the
 * question. Tab-scoped (sessionStorage); cleared when the tour finishes.
 */
export const welcomeTourSessionAtom = atomWithStorage<WelcomeTourSession | null>(
  'welcome-tour-session',
  null,
  createJSONStorage<WelcomeTourSession | null>(() =>
    typeof window === 'undefined' ? NO_STORAGE : window.sessionStorage
  ),
  { getOnInit: true }
)

/**
 * True once this session has loaded the user's guide state from the server (or given up
 * trying). Until then "not seen yet" may just mean "not loaded yet", so nothing opens by itself.
 */
export const guidePreferencesReadyAtom = atom(false)

/**
 * The plan the QA dock plays the tour (and the app's plan gates) as; null = the real one.
 * Non-prod only, tab-scoped like the tour session so a reload mid-tour keeps it.
 */
export const tourPlanPreviewAtom = atomWithStorage<TourPlan | null>(
  'welcome-tour-plan-preview',
  null,
  createJSONStorage<TourPlan | null>(() =>
    typeof window === 'undefined' ? NO_STORAGE : window.sessionStorage
  ),
  { getOnInit: true }
)
